import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Navigation, Ticket, Sparkles, Car, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useApi, http, gmapsDir, formatErr } from "@/lib/api";
import { Stars, RatingPill } from "@/components/site/DestinationCard";
import MapView from "@/components/site/MapView";
import { Textarea } from "@/components/ui/textarea";
import { Star, Play } from "lucide-react";
import { VideoRow } from "@/components/site/TikTokHook";
import { ReviewCard } from "@/components/site/Testimonials";

function ReviewForm({ slug, onAdded }) {
  const { t, money, tr } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return <button data-testid="login-to-review-btn" onClick={() => nav(`/login?next=/destinations/${slug}`)} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold hover:bg-slate-100">{t("login_to_review")}</button>;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await http.post(`/destinations/${slug}/reviews`, { rating, comment }); onAdded(data); setComment(""); toast.success(t("review_thanks")); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="review-form" className="space-y-3 rounded-3xl border border-slate-200 bg-white p-5">
      <p className="font-bold">{t("write_review")}</p>
      <div className="flex items-center gap-1">
        <span className="mr-2 text-sm text-slate-500">{t("your_rating")}</span>
        {[1, 2, 3, 4, 5].map((i) => <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => setRating(i)}><Star className={`h-6 w-6 transition-colors ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}
      </div>
      <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("review_ph")} rows={3} required minLength={3} />
      <button disabled={busy} data-testid="review-submit-btn" className="flex items-center gap-2 rounded-full bg-[#064E3B] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#047857] disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("submit_review")}</button>
    </form>
  );
}

function DestVideos({ slug }) {
  const { t, money, tr } = useLang();
  const { data } = useApi(`/videos?destination=${slug}`);
  if (!data?.length) return null;
  return (
    <section data-testid="dest-videos">
      <h2 className="text-2xl font-bold mb-1 flex items-center gap-2"><Play className="h-5 w-5 fill-[#E11D48] text-[#E11D48]" />{t("dest_videos")}</h2>
      <p className="mb-4 text-sm text-slate-500">{t("dest_videos_sub")}</p>
      <VideoRow videos={data} />
    </section>
  );
}

export default function DestinationDetail() {
  const { slug } = useParams();
  const { t, lang, money, tr } = useLang();
  const { data: d, setData } = useApi(`/destinations/${slug}`);
  if (!d) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>;
  const onAdded = (r) => setData({ ...d, rating: r.rating, review_count: r.review_count, reviews: [r.review, ...d.reviews].sort((a, b) => b.rating - a.rating || b.created_at.localeCompare(a.created_at)).slice(0, 10) });

  return (
    <div data-testid="destination-detail-modal" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8">
      <p className="text-sm text-slate-500"><Link to="/destinations" className="hover:underline">{t("nav_destinations")}</Link> / {d.name}</p>
      <h1 data-testid="dest-name" className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">{d.name}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
        <span data-testid="dest-rating"><RatingPill rating={d.rating} count={d.review_count} /></span>
        <span className="flex items-center gap-1 text-slate-600"><MapPin className="h-4 w-4" />{d.city}, Sumatera Barat</span>
      </div>
      <div className="mt-6 grid gap-3 md:grid-cols-4 md:grid-rows-2 h-[280px] md:h-[440px]">
        {d.gallery.slice(0, 3).map((g, i) => (
          <img key={i} src={g} alt={d.name} className={`h-full w-full rounded-2xl object-cover ${i === 0 ? "md:col-span-3 md:row-span-2" : "hidden md:block"}`} />
        ))}
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-10">
          <section><h2 className="text-2xl font-bold mb-3">{t("about")}</h2><p className="text-base leading-relaxed text-slate-700">{tr(d, "desc")}</p></section>
          <DestVideos slug={slug} />
          <section><h2 className="text-2xl font-bold mb-3">{t("location")}</h2><MapView points={[{ ...d, subtitle: d.city }]} zoom={12} height="h-[320px]" /></section>
          <section className="space-y-5">
            <div className="flex items-center gap-4"><span className="text-5xl font-extrabold">{Number(d.rating).toFixed(1)}</span><div><Stars value={d.rating} size="h-5 w-5" /><p className="text-sm text-slate-500">{d.review_count.toLocaleString()} {t("reviews")}</p></div></div>
            <ReviewForm slug={slug} onAdded={onAdded} />
            <div className="grid gap-4 md:grid-cols-2" data-testid="reviews-list">
              {d.reviews.map((r) => (
                <ReviewCard key={r.id} r={r} />
              ))}
            </div>
          </section>
        </div>
        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60">
            <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-slate-500"><Ticket className="h-4 w-4" />{t("ticket")}</span><b className="text-2xl">{d.price ? money(d.price, d.city_slug) : t("free")}</b></div>
            <a href={gmapsDir(d.lat, d.lng)} target="_blank" rel="noreferrer" data-testid="dest-directions-btn" className="flex items-center justify-center gap-2 rounded-full border border-slate-300 py-3 font-bold hover:bg-slate-50"><Navigation className="h-4 w-4" />{t("directions")}</a>
            <Link to="/planner" data-testid="dest-plan-btn" className="flex items-center justify-center gap-2 rounded-full bg-[#E11D48] py-3 font-bold text-white hover:bg-[#BE123C]"><Sparkles className="h-4 w-4" />{t("plan_trip_here")}</Link>
            <div className="rounded-2xl bg-emerald-50 p-4">
              <p className="flex items-center gap-2 font-bold text-[#064E3B]"><Car className="h-4 w-4" />{t("need_ride")}</p>
              <p className="mt-1 text-sm text-slate-600">{t("need_ride_sub")}</p>
              <Link to="/cars" data-testid="dest-rent-car-btn" className="mt-3 inline-block text-sm font-bold text-[#064E3B] underline">{t("nav_cars")} →</Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
